import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { useUID } from 'react-uid';
import { border, color, compose, layout, position, space } from 'styled-system';

import Flex from '../Flex';
import DropdownItem from './DropdownItem';

const baseDropdownStyles = ({ theme }) => css`
  background: ${theme.colors.dropdown.background};
  border-radius: ${theme.border.radius.size2};
  box-shadow: ${theme.shadows.size3};
  display: none;
  overflow: hidden;
  margin-top: ${theme.space[2]};
  position: absolute;
  top: 100%;
  width: fit-content;
  z-index: 2;
`;

const alignDropdownStyles = ({ align }) => css`
  left: ${align === 'left' && 0};
  right: ${align === 'right' && 0};
`;

const openDropdownStyles = ({ open }) =>
  open &&
  css`
    display: block;
  `;

const StyledDropdown = styled('div')`
  ${baseDropdownStyles}
  ${alignDropdownStyles}
  ${openDropdownStyles}
  ${compose(border, color, position, space, layout)};
`;

function Dropdown({
  id: customId,
  children,
  open,
  openerRef,
  renderToHTML,
  onClickOutside,
  ...rest
}) {
  const dropdownElem = useRef();
  const uniqueId = useUID();
  const id = customId || uniqueId;

  useEffect(() => {
    const handleClickOutside = ({ target }) => {
      if (
        dropdownElem.current &&
        !dropdownElem.current.contains(target) &&
        !openerRef.current.contains(target)
      ) {
        onClickOutside();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [dropdownElem, openerRef, onClickOutside]);

  if (!open && !renderToHTML) {
    return null;
  }

  return (
    <StyledDropdown open={open} ref={dropdownElem} {...rest}>
      <Flex
        flexDirection="column"
        role="menu"
        id={id}
        aria-labelledby={id}
        /**
         * is used to suppress errors related of inconsistencies between
         * client-side and server-side of ids generated by 'react-uid'
         */
        suppressHydrationWarning
      >
        {children}
      </Flex>
    </StyledDropdown>
  );
}

Dropdown.defaultProps = {
  align: 'left',
  id: null,
  open: false,
  renderToHTML: false,
  onClick: () => {},
  onClickOutside: () => {},
};

Dropdown.propTypes = {
  /**
   * Align component by parent's left or right side
   */
  align: PropTypes.oneOf(['left', 'right']),
  /**
   * Custom identifier for the Dropdown. If this is not provided,
   * we will add a unique id with the prefix `dropdown_`
   */
  id: PropTypes.string,
  /**
   * `DropdownItem`s to be rendered as children
   */
  children: PropTypes.node.isRequired,
  /**
   * Whether the dropdown menu is open
   */
  open: PropTypes.bool,
  /**
   * The ref of the component that is being used to open the dropdown
   */
  openerRef: PropTypes.shape().isRequired,
  /**
   * Whether to render the dropdown to HTML even if its not opened, may be used
   * for SEO purposes
   */
  renderToHTML: PropTypes.bool,
  /**
   * Callback function on click
   */
  onClick: PropTypes.func,
  /**
   * Callback function on click outside of the dropdown. Normally used to
   * close it
   */
  onClickOutside: PropTypes.func,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
  /**
   * Implements `styled-system` [position](https://styled-system.com/api/#position) utility
   */
  ...propTypes.position,
};

export { Dropdown, DropdownItem };
